import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { useDirectionStore } from "@/store/directionStore"
import type { Direction } from "@/store/directionStore"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[colors,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// Direction-aware overlay classes. Returns '' for 'current' so the base cva output is byte-identical.
function directionOverlay(
  direction: Direction,
  variant: string | null | undefined,
  size: string | null | undefined
): string {
  const v = variant ?? 'default'
  const s = size ?? 'default'

  if (direction === 'command') {
    // outline + ghost get mono treatment; all others unchanged
    if (v === 'outline' || v === 'ghost') {
      return 'font-mono uppercase tracking-wide text-xs'
    }
    return ''
  }

  if (direction === 'saas') {
    // Two notches shorter, denser padding on all sizes + shadow-none + text-[13px]
    const sizeOverlay: Record<string, string> = {
      default: 'h-8 px-3',
      sm: 'h-7 px-2.5',
      lg: 'h-9 px-6',
      icon: 'h-8 w-8',
    }
    return cn(sizeOverlay[s] ?? '', 'shadow-none text-[13px]')
  }

  if (direction === 'expressive') {
    // All variants get rounded-xl; default variant gets gradient + hover override
    if (v === 'default') {
      return 'grad-accent text-black border-0 shadow-elevation-1 hover:shadow-elevation-2 hover:bg-transparent rounded-xl'
    }
    return 'rounded-xl'
  }

  return ''
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const direction = useDirectionStore(s => s.direction)
    const overlay = directionOverlay(direction, variant, size)
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), overlay, className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
